import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Avatar,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import ManagerApi from '../../api/ManagerApi';
import { CourtDto, SearchFilters } from '../../interfaces/CourtInterfaces';
import './ManagerCourtsView.scss';

const initialFilters: SearchFilters = {
  Court_Name: '',
  Court_Address: '',
  Court_Email: '',
  Court_Phone: '',
  Court_Fax: '',
  search: ''
};

const ManagerCourtsView: React.FC = () => {
  const navigate = useNavigate();
  const [courts, setCourts] = useState<CourtDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);

  const RESULTS_PER_PAGE = 20;

  const searchCourts = useCallback(async (searchFilters = filters, page = 0, appendResults = false) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    
    try {
      const from = page * RESULTS_PER_PAGE;
      const result = await ManagerApi.getCourts(searchFilters, from, RESULTS_PER_PAGE);
      
      if (appendResults) {
        setCourts(prevCourts => [...prevCourts, ...result.courts]);
      } else {
        setCourts(result.courts);
      }
      
      // Check if there are more results
      setHasMoreData(result.courts.length === RESULTS_PER_PAGE);
      setCurrentPage(page);
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search courts');
      if (!appendResults) {
        setCourts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [RESULTS_PER_PAGE]);

  useEffect(() => {
    searchCourts(initialFilters, 0, false);
  }, [searchCourts]);

  const handleSearch = () => {
    setCurrentPage(0);
    setHasMoreData(false);
    searchCourts(filters, 0, false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    searchCourts(filters, nextPage, true);
  };

  const clearSearch = useCallback(() => {
    setFilters(initialFilters);
    setCurrentPage(0);
    setHasMoreData(false);
    setError(null);
    searchCourts(initialFilters, 0, false);
  }, [searchCourts]);

  const handleFilterChange = useCallback((key: keyof SearchFilters) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters(prev => ({
      ...prev,
      [key]: event.target.value
    }));
  }, []);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="manager-courts-view">
      
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Courts Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-court')}
            sx={{ height: 'fit-content' }}
          >
            Add New Court
          </Button>
        </Box>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Courts
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="General Search"
              placeholder="Search by court name or address..."
              value={filters.search}
              onChange={handleFilterChange('search')}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
          </Box>

          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Or search by specific fields:
          </Typography>
          
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              label="Court Name"
              value={filters.Court_Name}
              onChange={handleFilterChange('Court_Name')}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
            
            <TextField
              label="Court Email"
              value={filters.Court_Email}
              onChange={handleFilterChange('Court_Email')}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
            
            <TextField
              label="Court Phone"
              value={filters.Court_Phone}
              onChange={handleFilterChange('Court_Phone')}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />

            <TextField
              label="Court Fax"
              value={filters.Court_Fax}
              onChange={handleFilterChange('Court_Fax')}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
            
            <TextField
              label="Court Address"
              value={filters.Court_Address}
              onChange={handleFilterChange('Court_Address')}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
          </Box>

          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              disabled={loading}
            >
              Search Courts
            </Button>
            
            <Button
              variant="outlined"
              onClick={clearSearch}
              startIcon={<ClearIcon />}
              disabled={loading}
            >
              Clear
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {hasSearched && !loading && (
          <Paper elevation={3}>
            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                Search Results ({courts.length} courts found)
              </Typography>
              
              {courts.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No courts found matching your search criteria.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Court Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Fax</TableCell>
                          <TableCell>Address</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {courts.map((court) => (
                          <TableRow
                            key={court.Court_guid}
                            component={Link}
                            to={`/court?Court_guid=${court.Court_guid}`}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                              cursor: 'pointer',
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}>
                                  <AccountBalanceIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {court.Court_Name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{court.Court_Email}</TableCell>
                            <TableCell>{court.Court_Phone || 'N/A'}</TableCell>
                            <TableCell>{court.Court_Fax || 'N/A'}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                                {court.Court_Address}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Load More Button */}
                  {hasMoreData && (
                    <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
                      >
                        {loadingMore ? 'Loading...' : `Load More (Showing ${courts.length} results)`}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>
        )}

      </Container>
    </div>
  );
};

export default ManagerCourtsView;
